import type { OrganizationScope, ViewScopeMode } from "@/types/organization-scope"
import { defineStore } from "pinia"
import { computed, ref } from "vue"
import { recordBusinessContextTrace } from "@/utils/businessContext/diagnostics"
import { isOrganizationScopeSwitchable } from "@/utils/organizationScopeSwitch"

/**
 * useViewScopeStore — 单店 / 总部视图状态(SLICE-002 IA 骨架)
 *
 * 跟 2b-miniapp utils/business-context-switcher.js 对齐:
 * - storage key 'active_organization_scope'
 * - value 格式 shop-{id} / chain-headquarter-{id} / chain-region-{id} / chain-store-{id}
 * - 后端 /teashop_merchant/me/organizations 通过 setOrganizations() 注入真实数据
 *
 * 业务模块通过 useViewScope() composable 读 viewMode/activeScope。
 * 授权范围由当前 OAuth token 承载,不再从 viewScope 派生 X-Scope-* 请求头。
 */

export const ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY = "active_organization_scope"
export const ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY = "active_organization_scope_account"
export const ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY = "active_organization_scope_by_account"

type RememberedScopeMap = Record<string, string>

function readStorageValue(key: string) {
  try {
    return localStorage.getItem(key) || ""
  } catch {
    return ""
  }
}

function writeStorageValue(key: string, value: string) {
  try {
    if (value) {
      localStorage.setItem(key, value)
    } else {
      localStorage.removeItem(key)
    }
    return true
  } catch {
    // localStorage may be unavailable in private mode.
    return false
  }
}

function readRememberedScopeMap(): RememberedScopeMap {
  try {
    const raw = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY)
    if (!raw) return {}
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {}
    return Object.fromEntries(
      Object.entries(parsed)
        .filter((entry): entry is [string, string] => Boolean(entry[0]) && typeof entry[1] === "string" && Boolean(entry[1])),
    )
  } catch {
    return {}
  }
}

function writeRememberedScopeMap(value: RememberedScopeMap) {
  const entries = Object.entries(value).filter(([, scopeId]) => Boolean(scopeId))
  return writeStorageValue(
    ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY,
    entries.length > 0 ? JSON.stringify(Object.fromEntries(entries)) : "",
  )
}

function readRememberedScope(accountId: string) {
  if (accountId) {
    const mappedScopeId = readRememberedScopeMap()[accountId]
    if (mappedScopeId) return mappedScopeId
  }
  return readStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY)
}

function saveCompatibilityScope(accountId: string, scopeId: string) {
  let committed = true
  if (accountId) {
    committed = writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY, accountId) && committed
  }
  return writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY, scopeId) && committed
}

interface SetOrganizationsOptions {
  autoSelect?: boolean
}

interface BindAccountOptions {
  preserveRuntimeOnInitialBind?: boolean
  preserveRuntimeOnAccountChange?: boolean
}

function getScopeShopId(scope: OrganizationScope | undefined): string {
  const shopId = scope?.meta?.shopId
  return shopId == null ? "" : String(shopId)
}

function findEnabledChainStoreTwin(items: OrganizationScope[], shopScope: OrganizationScope | undefined) {
  const shopId = getScopeShopId(shopScope)
  if (!shopId) return undefined
  return items.find(item =>
    isOrganizationScopeSwitchable(item)
    && item.type === "chain-store"
    && getScopeShopId(item) === shopId,
  )
}

function normalizeOrganizationScopes(items: OrganizationScope[]) {
  const chainStoreShopIds = new Set(
    items
      .filter(item => isOrganizationScopeSwitchable(item) && item.type === "chain-store")
      .map(item => getScopeShopId(item))
      .filter(Boolean),
  )
  return items.filter(item =>
    !(item.type === "shop" && chainStoreShopIds.has(getScopeShopId(item))),
  )
}

function findDefaultOrganization(items: OrganizationScope[]) {
  const switchableItems = items.filter(item => isOrganizationScopeSwitchable(item))
  return switchableItems.find(item => item.type === "chain-headquarter")
    ?? switchableItems.find(item => item.type === "chain-region")
    ?? switchableItems.find(item => item.type === "shop" || item.type === "chain-store")
    ?? switchableItems[0]
}

export const useViewScopeStore = defineStore("viewScope", () => {
  /** 当前登录账号；持久化选择必须按账号隔离。 */
  const accountId = ref(readStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY))

  /** 持久化偏好只参与候选选择，不能冒充当前 token 已确认的运行态。 */
  const preferredScopeId = ref<string>(readRememberedScope(accountId.value))
  /**
   * 旧稳定制品只会更新全局兼容 key。若该 key 与同一账号的已提交偏好不同，
   * 将差异作为一次跨制品 handoff 候选；controller 仍必须完成 scope switch
   * 和 token exchange，不能把它当作当前 token 已确认的运行态。
   */
  const legacyArtifactHandoffScopeId = computed(() => {
    const currentAccountId = accountId.value.trim()
    if (!currentAccountId) return ""
    if (
      readStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY)
      !== currentAccountId
    ) {
      return ""
    }
    const compatibilityScopeId = readStorageValue(
      ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY,
    )
    return compatibilityScopeId
      && compatibilityScopeId !== preferredScopeId.value
      ? compatibilityScopeId
      : ""
  })
  /** 当前 token 已确认并由上下文 controller 发布的运行态 scope。 */
  const activeScopeId = ref<string>("")
  /** 已提交 scope 的不可变快照；候选机构列表刷新不能让当前页面丢失运行态 scope。 */
  const activeScopeSnapshot = ref<OrganizationScope>()

  /** 业务上下文刷新版本,用于切换机构后强制重挂载当前业务页 */
  const contextVersion = ref(0)

  /** 正在切换机构视图;旧页面监听到 scope 变化时用它跳过无效请求 */
  const contextSwitching = ref(false)
  let contextSwitchLeaseCount = 0
  /** 跨标签同步到的已提交偏好；仅供下一次事务选择，不直接发布为运行时 scope。 */
  const pendingPreferredScopeId = ref("")

  /** 可切换的 organizations 列表 — 以后端 /me/organizations 为准 */
  const organizations = ref<OrganizationScope[]>([])

  /** organizations 是否已从后端权威接口加载过，空数组也必须视为已解析后 fail-closed */
  const organizationsResolved = ref(false)

  recordBusinessContextTrace("view-scope.store-created", {
    activeScopeId: activeScopeId.value,
    preferredScopeId: preferredScopeId.value,
    hadKnownAccount: Boolean(accountId.value),
    rememberedScopeIds: Object.values(readRememberedScopeMap()),
    compatibilityScopeId: readStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY),
  })

  const activeScope = computed<OrganizationScope | undefined>(() =>
    organizations.value.find(o => o.id === activeScopeId.value)
    ?? (
      activeScopeSnapshot.value?.id === activeScopeId.value
        ? activeScopeSnapshot.value
        : undefined
    ),
  )

  const viewMode = computed<ViewScopeMode>(() => {
    if (activeScope.value?.type === "chain-headquarter" || activeScope.value?.type === "chain-region") return "hq"
    return "shop"
  })

  const activeRegionId = computed<number | undefined>(() => {
    if (activeScope.value?.type !== "chain-region") return undefined
    const regionId = activeScope.value.meta?.regionId
    if (regionId != null) return regionId
    const matched = /^chain-region-(\d+)$/.exec(activeScope.value.id)
    return matched ? Number(matched[1]) : undefined
  })

  const isRegionScope = computed(() => activeScope.value?.type === "chain-region")

  function setActive(id: string, source = "direct") {
    const previousScopeId = activeScopeId.value
    activeScopeId.value = id
    activeScopeSnapshot.value = id
      ? organizations.value.find(item => item.id === id) ?? activeScopeSnapshot.value
      : undefined
    recordBusinessContextTrace("view-scope.active-changed", {
      source,
      previousScopeId,
      nextScopeId: id,
      organizationsResolved: organizationsResolved.value,
    })
  }

  function clearRememberedActive(expectedScopeId = "") {
    if (accountId.value) {
      const rememberedScopes = readRememberedScopeMap()
      if (!expectedScopeId || rememberedScopes[accountId.value] === expectedScopeId) {
        delete rememberedScopes[accountId.value]
        writeRememberedScopeMap(rememberedScopes)
      }
    }
    const compatibilityScopeId = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY)
    const compatibilityAccountId = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY)
    if (
      (!expectedScopeId || compatibilityScopeId === expectedScopeId)
      && (!accountId.value || !compatibilityAccountId || compatibilityAccountId === accountId.value)
    ) {
      writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY, "")
    }
  }

  function commitActive(id = activeScopeId.value) {
    return commitContext(accountId.value, id)
  }

  function commitContext(nextAccountId: string, id = activeScopeId.value) {
    const scope = organizations.value.find(item => item.id === id)
    if (!id || !scope || !isOrganizationScopeSwitchable(scope)) {
      recordBusinessContextTrace("view-scope.preference-rejected", {
        scopeId: id,
        hasScope: Boolean(scope),
        switchable: Boolean(scope && isOrganizationScopeSwitchable(scope)),
      })
      return false
    }

    // A prepared role snapshot can temporarily omit its principal. Once the
    // authenticated account is known, never demote the organization preference
    // back to the legacy global bucket or F5 may restore another scope.
    const normalizedAccountId = nextAccountId.trim() || accountId.value.trim()
    const previousRememberedMap = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY)
    const previousCompatibilityAccount = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY)
    const previousCompatibilityScope = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY)
    let committed = true
    if (normalizedAccountId) {
      const rememberedScopes = readRememberedScopeMap()
      rememberedScopes[normalizedAccountId] = id
      committed = writeRememberedScopeMap(rememberedScopes) && committed
    }
    committed = saveCompatibilityScope(normalizedAccountId, id) && committed
    if (!committed) {
      writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_BY_ACCOUNT_STORAGE_KEY, previousRememberedMap)
      writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY, previousCompatibilityAccount)
      writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY, previousCompatibilityScope)
      accountId.value = normalizedAccountId
      pendingPreferredScopeId.value = ""
      recordBusinessContextTrace("view-scope.preference-write-failed", {
        scopeId: id,
        hadKnownAccount: Boolean(normalizedAccountId),
      })
      return false
    }
    accountId.value = normalizedAccountId
    preferredScopeId.value = id
    pendingPreferredScopeId.value = ""
    recordBusinessContextTrace("view-scope.preference-committed", {
      scopeId: id,
      hadKnownAccount: Boolean(normalizedAccountId),
    })
    return true
  }

  function stagePreferredOrganization(id: string) {
    pendingPreferredScopeId.value = id
  }

  function bindAccount(nextAccountId: string, options: BindAccountOptions = {}) {
    const normalizedAccountId = nextAccountId.trim()
    if (!normalizedAccountId) return
    const previousScopeId = activeScopeId.value
    const accountChanged = normalizedAccountId !== accountId.value
    if (normalizedAccountId === accountId.value) {
      preferredScopeId.value = readRememberedScope(normalizedAccountId)
      recordBusinessContextTrace("view-scope.account-bound", {
        accountChanged,
        previousScopeId,
        nextScopeId: activeScopeId.value,
        preferredScopeId: preferredScopeId.value,
        mode: "same-account",
      })
      return
    }

    if (options.preserveRuntimeOnAccountChange) {
      if (!writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY, normalizedAccountId)) {
        return false
      }
      accountId.value = normalizedAccountId
      preferredScopeId.value = readRememberedScope(normalizedAccountId)
      pendingPreferredScopeId.value = ""
      recordBusinessContextTrace("view-scope.account-bound", {
        accountChanged,
        previousScopeId,
        nextScopeId: activeScopeId.value,
        mode: "preserve-runtime",
      })
      return true
    }

    const isInitialAccountBind = !accountId.value
    const previousStorageAccountId = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY)
    const rememberedScopes = readRememberedScopeMap()
    let rememberedScopeId = rememberedScopes[normalizedAccountId] ?? ""

    // One-time migration for installations that only have the legacy global key.
    if (!rememberedScopeId && !previousStorageAccountId) {
      rememberedScopeId = readStorageValue(ACTIVE_ORGANIZATION_SCOPE_STORAGE_KEY)
      if (rememberedScopeId) {
        rememberedScopes[normalizedAccountId] = rememberedScopeId
        writeRememberedScopeMap(rememberedScopes)
      }
    }

    accountId.value = normalizedAccountId
    preferredScopeId.value = rememberedScopeId
    pendingPreferredScopeId.value = ""
    if (isInitialAccountBind && options.preserveRuntimeOnInitialBind) {
      writeStorageValue(ACTIVE_ORGANIZATION_SCOPE_ACCOUNT_STORAGE_KEY, normalizedAccountId)
      recordBusinessContextTrace("view-scope.account-bound", {
        accountChanged,
        previousScopeId,
        nextScopeId: activeScopeId.value,
        mode: "initial-preserve-runtime",
      })
      return
    }
    activeScopeId.value = ""
    activeScopeSnapshot.value = undefined
    organizations.value = []
    organizationsResolved.value = false
    saveCompatibilityScope(normalizedAccountId, rememberedScopeId)
    recordBusinessContextTrace("view-scope.account-bound", {
      accountChanged,
      previousScopeId,
      nextScopeId: activeScopeId.value,
      mode: "restore-remembered",
    })
  }

  function getPreferredOrganizationId() {
    const pendingOrganization = organizations.value.find(item =>
      item.id === pendingPreferredScopeId.value && isOrganizationScopeSwitchable(item),
    )
    if (pendingOrganization) return pendingOrganization.id
    const preferredOrganization = organizations.value.find(item =>
      item.id === preferredScopeId.value && isOrganizationScopeSwitchable(item),
    )
    if (preferredOrganization) return preferredOrganization.id
    const activeOrganization = organizations.value.find(item =>
      item.id === activeScopeId.value && isOrganizationScopeSwitchable(item),
    )
    return activeOrganization?.id ?? findDefaultOrganization(organizations.value)?.id ?? ""
  }

  function setOrganizations(items: OrganizationScope[], options: SetOrganizationsOptions = {}) {
    const previousScopeId = activeScopeId.value
    const normalizedItems = normalizeOrganizationScopes(items)
    organizations.value = normalizedItems
    organizationsResolved.value = true

    const preferredRawScope = items.find(item =>
      item.id === (
        pendingPreferredScopeId.value
        || preferredScopeId.value
        || activeScopeId.value
      ),
    )
    const canonicalPreferredScope = preferredRawScope?.type === "shop"
      ? findEnabledChainStoreTwin(normalizedItems, preferredRawScope)
      : undefined

    // A transaction may refresh the available organizations while the committed
    // context is still serving the current page. In that case the list is only
    // staged data: it must not rewrite or clear the committed active scope.
    const autoSelect = options.autoSelect ?? true
    if (!autoSelect) {
      if (canonicalPreferredScope) {
        pendingPreferredScopeId.value = canonicalPreferredScope.id
      }
      recordBusinessContextTrace("view-scope.organizations-staged", {
        count: normalizedItems.length,
        previousScopeId,
        activeScopeId: activeScopeId.value,
        pendingPreferredScopeId: pendingPreferredScopeId.value,
      })
      return
    }

    if (canonicalPreferredScope) {
      setActive(canonicalPreferredScope.id, "organizations:canonical-preference")
    }
    const activeSwitchable = normalizedItems.some(o => o.id === activeScopeId.value && isOrganizationScopeSwitchable(o))
    if (activeScopeId.value && !activeSwitchable) {
      setActive("", "organizations:active-not-switchable")
    } else if (activeSwitchable) {
      activeScopeSnapshot.value = normalizedItems.find(item => item.id === activeScopeId.value)
    }
    if (!activeScopeId.value) {
      const preferredOrganization = normalizedItems.find(item =>
        item.id === (pendingPreferredScopeId.value || preferredScopeId.value)
        && isOrganizationScopeSwitchable(item),
      )
      const selectedOrganization = preferredOrganization
        ?? findDefaultOrganization(normalizedItems)
      if (selectedOrganization) {
        setActive(
          selectedOrganization.id,
          preferredOrganization
            ? "organizations:preference"
            : "organizations:default",
        )
      }
    }
    recordBusinessContextTrace("view-scope.organizations-applied", {
      count: normalizedItems.length,
      previousScopeId,
      activeScopeId: activeScopeId.value,
    })
  }

  function reset(
    options: { forgetRemembered?: boolean } = {},
    source = "direct",
  ) {
    const previousScopeId = activeScopeId.value
    activeScopeId.value = ""
    activeScopeSnapshot.value = undefined
    organizations.value = []
    organizationsResolved.value = false
    pendingPreferredScopeId.value = ""
    contextSwitchLeaseCount = 0
    contextSwitching.value = false
    if (options.forgetRemembered) {
      clearRememberedActive()
      preferredScopeId.value = ""
    }
    recordBusinessContextTrace("view-scope.runtime-reset", {
      source,
      previousScopeId,
      forgetRemembered: options.forgetRemembered === true,
    })
  }

  function bumpContextVersion() {
    contextVersion.value += 1
  }

  function beginContextSwitch() {
    contextSwitchLeaseCount += 1
    contextSwitching.value = contextSwitchLeaseCount > 0
  }

  function endContextSwitch() {
    contextSwitchLeaseCount = Math.max(0, contextSwitchLeaseCount - 1)
    contextSwitching.value = contextSwitchLeaseCount > 0
  }

  return {
    accountId,
    preferredScopeId,
    legacyArtifactHandoffScopeId,
    activeScopeId,
    contextVersion,
    contextSwitching,
    organizations,
    organizationsResolved,
    activeScope,
    activeRegionId,
    isRegionScope,
    viewMode,
    bindAccount,
    setActive,
    stagePreferredOrganization,
    commitActive,
    commitContext,
    clearRememberedActive,
    getPreferredOrganizationId,
    setOrganizations,
    bumpContextVersion,
    beginContextSwitch,
    endContextSwitch,
    reset,
  }
})

export function useViewScopeStoreHook() {
  return useViewScopeStore()
}
